import { useAssistantRefresh } from '../hooks/useAssistantRefresh';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatTaskKey,
  TASK_PRIORITY_LABEL,
  TASK_STATUS_LABEL,
  type ListSummary,
  type TaskSummary,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { listApi } from '../api/list.api';
import { taskApi } from '../api/task.api';
import { useTaskActions } from '../hooks/useTaskActions';
import { PRIORITY_BADGE, STATUS_TONE, groupByDue, initials } from '../lib/task-ui';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import { SidePanel } from '../components/SidePanel';
import { TaskForm } from '../components/TaskForm';
import { StarButton } from '../components/StarButton';
import { TaskListSkeleton, TaskListError, TaskListEmpty } from '../components/TaskCard';

/**
 * One task as a ROW - this page reads like a checklist, not a board:
 * complete-circle, title over key & department, status pill, priority
 * badge, sub-task progress, assignee, star, and Remove (from this list).
 */
function ListTaskRow({
  task, busy, onOpen, onToggleDone, onToggleImportant, onRemove,
}: {
  task: TaskSummary;
  busy: boolean;
  onOpen: (task: TaskSummary) => void;
  onToggleDone: (task: TaskSummary) => void;
  onToggleImportant: (task: TaskSummary) => void;
  onRemove: (task: TaskSummary) => void;
}) {
  const tone = STATUS_TONE[task.status];
  const done = task.status === 'DONE';
  const closed = done || task.status === 'CANCELLED';
  const first = task.assignees[0];
  const pct = task.subtaskCount > 0
    ? Math.round((task.subtaskDoneCount / task.subtaskCount) * 100)
    : 0;

  return (
    <li
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onOpen(task);
      }}
      className={[
        'flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-l-4 border-slate-200 bg-white px-4 py-3 transition hover:border-slate-300 hover:shadow-sm',
        tone.edge,
        closed ? 'opacity-80' : '',
      ].join(' ')}
    >
      <button
        type="button"
        disabled={busy || task.status === 'CANCELLED'}
        title={done ? 'Reopen task' : 'Mark complete'}
        onClick={() => onToggleDone(task)}
        className={[
          'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold transition disabled:opacity-40',
          done
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'border-2 border-slate-300 bg-white text-transparent hover:border-emerald-500 hover:text-emerald-500',
        ].join(' ')}
      >
        &#10003;
      </button>

      <div className="min-w-0 flex-1 basis-48">
        <p className={[
          'truncate text-sm font-semibold',
          done ? 'text-slate-400 line-through' : 'text-slate-900',
        ].join(' ')}>
          {task.title}
        </p>
        <p className="mt-0.5 truncate text-xs text-slate-400">
          <span className="font-mono">{formatTaskKey(task.taskKey)}</span>
          {task.departmentName && <> &middot; {task.departmentName}</>}
        </p>
      </div>

      <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone.pill}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
        {TASK_STATUS_LABEL[task.status]}
      </span>

      <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${PRIORITY_BADGE[task.priority]}`}>
        {TASK_PRIORITY_LABEL[task.priority]}
      </span>

      <div className="hidden w-36 shrink-0 sm:block">
        {task.subtaskCount > 0 ? (
          <>
            <div className="h-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-emerald-400" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1 text-[11px] tabular-nums text-slate-400">
              {task.subtaskDoneCount} of {task.subtaskCount} sub-tasks
            </p>
          </>
        ) : (
          <p className="text-[11px] text-slate-400">No sub-tasks</p>
        )}
      </div>

      {/* One NON-wrapping cluster pinned right, so a long assignee name can
          never push the star or Remove onto their own line - the name
          truncates instead. */}
      <div className="ml-auto flex shrink-0 items-center gap-3">
        {first && (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"
            title={task.assignees.map((a) => a.displayName).join(', ')}
          >
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-brand-100 text-[9px] font-bold text-brand-700">
              {initials(first.displayName)}
            </span>
            <span className="hidden max-w-24 truncate lg:inline">
              {first.displayName.split(/\s+/)[0]}
              {task.assignees.length > 1 && ` +${task.assignees.length - 1}`}
            </span>
          </span>
        )}

        <StarButton
          active={task.isImportant}
          busy={busy}
          onToggle={() => onToggleImportant(task)}
        />

        <button
          type="button"
          onClick={() => onRemove(task)}
          title="Take this task out of this list (the task itself is not deleted)"
          className="text-xs font-medium text-slate-400 transition hover:text-red-600"
        >
          &times; Remove
        </button>
      </div>
    </li>
  );
}

/**
 * Module 3 - Lists.
 *
 * Personal labels over tasks: each list belongs to the signed-in user alone,
 * and one task can sit in many lists. The rail owns the lists; the main pane
 * shows the selected list's tasks with the same cards as everywhere else.
 * Tasks are ADDED from the task panel's Lists row - this page is for reading
 * and managing, not filing.
 */
export default function ListsPage() {
  const [lists, setLists] = useState<ListSummary[] | null>(null);
  /** The "Your lists" card folds away behind its chevron. */
  const [railOpen, setRailOpen] = useState(true);
  /** Per-group fold state for Due today / Upcoming / ... sections. */
  const [foldedGroups, setFoldedGroups] = useState<Record<string, boolean>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [railError, setRailError] = useState<string | null>(null);

  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);
  const [creating, setCreating] = useState(false);

  function replace(updated: TaskSummary) {
    setTasks((current) => current.map((t) => (t.id === updated.id ? updated : t)));
  }

  const actions = useTaskActions(replace, setFlash);

  const loadLists = useCallback(async () => {
    setRailError(null);
    try {
      const rows = await listApi.all();
      setLists(rows);
      // Keep a valid selection: first list by default, none when empty.
      setSelectedId((current) =>
        current && rows.some((l) => l.id === current) ? current : (rows[0]?.id ?? null));
    } catch (err) {
      setRailError(err instanceof ApiError ? err.message : 'Could not load your lists');
      setLists((current) => current ?? []);
    }
  }, []);

  useEffect(() => { void loadLists(); }, [loadLists]);

  // The AI chat can change data server-side; refetch when it says so.
  useAssistantRefresh(loadLists);

  const loadTasks = useCallback(async () => {
    if (!selectedId) { setTasks([]); return; }
    setTasksLoading(true);
    setTasksError(null);
    try {
      const res = await taskApi.list({ listId: selectedId, pageSize: 100 });
      setTasks(res.data);
    } catch (err) {
      setTasksError(err instanceof ApiError ? err.message : 'Could not load the list');
    } finally {
      setTasksLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { void loadTasks(); }, [loadTasks]);

  async function createList() {
    const name = newName.trim();
    if (!name) return;
    try {
      setLists(await listApi.create(name));
      setNewName('');
    } catch (err) {
      setRailError(err instanceof ApiError ? err.message : 'Could not create the list');
    }
  }

  async function saveRename(list: ListSummary) {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name || name === list.name) return;
    try {
      setLists(await listApi.rename(list.id, name));
    } catch (err) {
      setRailError(err instanceof ApiError ? err.message : 'Could not rename the list');
    }
  }

  async function removeList(list: ListSummary) {
    setConfirmDeleteId(null);
    try {
      await listApi.remove(list.id);
      await loadLists();
    } catch (err) {
      setRailError(err instanceof ApiError ? err.message : 'Could not delete the list');
    }
  }

  async function removeFromList(task: TaskSummary) {
    if (!selectedId) return;
    try {
      setLists(await listApi.removeTask(selectedId, task.id));
      setTasks((current) => current.filter((t) => t.id !== task.id));
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not remove the task from the list');
    }
  }

  /** A task born on this page lands straight in the selected list. */
  async function fileNewTask(task: TaskSummary) {
    setCreating(false);
    if (!selectedId) return;
    try {
      setLists(await listApi.addTask(selectedId, task.id));
      setFlash(`${formatTaskKey(task.taskKey)} created and added to this list.`);
      await loadTasks();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Created the task, but could not add it to the list');
      await loadTasks();
    }
  }

  const selected = lists?.find((l) => l.id === selectedId) ?? null;
  const groups = useMemo(() => groupByDue(tasks), [tasks]);
  const dueTodayCount = useMemo(
    () => groups.find((g) => g.key === 'today')?.tasks.length ?? 0,
    [groups],
  );

  return (
    <div className="mx-auto max-w-7xl px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Lists</h1>
        <p className="mt-1 text-sm text-slate-500">
          Your own way of grouping tasks - only you see these. Add tasks from any
          task&apos;s panel.
        </p>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[320px_1fr]">
        {/* ---- Rail: the "Your lists" card ---- */}
        <aside className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-semibold text-slate-900">Your lists</h2>
            <span className="text-sm font-semibold tabular-nums text-slate-400">
              {lists?.length ?? 0}
            </span>
            <button
              type="button"
              onClick={() => setRailOpen((open) => !open)}
              aria-expanded={railOpen}
              aria-label={railOpen ? 'Collapse your lists' : 'Expand your lists'}
              className="ml-auto rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"
                className={`h-4 w-4 transition-transform ${railOpen ? '' : 'rotate-180'}`}>
                <path d="M12.5 5L7.5 10l5 5" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
              <path d="M6 9V6.5a4 4 0 118 0V9M5 9h10v7H5V9z" stroke="currentColor"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Only you can see these
          </p>

          {railOpen && (
            <>
              <form
                onSubmit={(e) => { e.preventDefault(); void createList(); }}
                className="mt-3 flex gap-1.5"
              >
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New list name"
                  maxLength={120}
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                />
                <button type="submit" disabled={!newName.trim()}
                  className="rounded-lg bg-brand-600 px-2.5 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
                  Add
                </button>
              </form>
              <div className="mt-3 border-t border-slate-100" />
            </>
          )}

          {railError && (
            <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs text-red-700 ring-1 ring-red-100">
              {railError}
            </p>
          )}

          {railOpen && (
          <ul className="mt-2 space-y-0.5">
            {lists === null ? (
              [0, 1, 2].map((i) => (
                <li key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />
              ))
            ) : lists.length === 0 ? (
              <li className="px-2 py-6 text-center text-sm text-slate-400">
                No lists yet - name one above.
              </li>
            ) : (
              lists.map((list) => (
                <li key={list.id}>
                  {renamingId === list.id ? (
                    <form
                      onSubmit={(e) => { e.preventDefault(); void saveRename(list); }}
                      className="flex gap-1.5 px-1 py-0.5"
                    >
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => void saveRename(list)}
                        maxLength={120}
                        className="min-w-0 flex-1 rounded-lg border border-brand-400 px-2 py-1 text-sm outline-none ring-2 ring-brand-100"
                      />
                    </form>
                  ) : (
                    <div
                      className={[
                        'group flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition',
                        selectedId === list.id
                          ? 'bg-brand-50 font-semibold text-brand-700'
                          : 'text-slate-700 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      <button type="button" onClick={() => setSelectedId(list.id)}
                        className="min-w-0 flex-1 truncate text-left">
                        {list.name}
                      </button>
                      <span className="text-sm font-semibold tabular-nums text-slate-400">
                        {list.taskCount}
                      </span>
                      {confirmDeleteId === list.id ? (
                        <span className="flex items-center gap-1">
                          <button type="button" onClick={() => void removeList(list)}
                            className="rounded bg-red-600 px-1.5 py-0.5 text-[11px] font-semibold text-white hover:bg-red-700">
                            Delete
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteId(null)}
                            className="rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100">
                            Keep
                          </button>
                        </span>
                      ) : (
                        <span className="hidden items-center gap-0.5 group-hover:flex">
                          <button type="button" title="Rename"
                            onClick={() => { setRenamingId(list.id); setRenameValue(list.name); }}
                            className="rounded px-1 py-0.5 text-[11px] text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                            Rename
                          </button>
                          <button type="button" title="Delete list" aria-label={`Delete ${list.name}`}
                            onClick={() => setConfirmDeleteId(list.id)}
                            className="rounded px-1 py-0.5 text-[11px] text-slate-400 hover:bg-red-50 hover:text-red-600">
                            &times;
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                </li>
              ))
            )}
          </ul>
          )}
        </aside>

        {/* ---- Selected list's tasks ---- */}
        <div className="space-y-6">
          {selected && (
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{selected.name}</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                  {dueTodayCount > 0 && <> &middot; {dueTodayCount} due today</>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="shrink-0 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
              >
                + New task in this list
              </button>
            </div>
          )}
          {!selected ? (
            <TaskListEmpty
              title="No list selected"
              hint="Create a list on the left, then add tasks to it from any task's panel."
            />
          ) : tasksError ? (
            <TaskListError message={tasksError} onRetry={() => void loadTasks()} />
          ) : tasksLoading ? (
            <TaskListSkeleton />
          ) : tasks.length === 0 ? (
            <TaskListEmpty
              title={`Nothing in "${selected.name}" yet`}
              hint="Use the button above, or open any task and tick this list under its Lists row."
            />
          ) : (
            groups.map((group) => {
              const folded = !!foldedGroups[group.key];
              return (
                <section key={group.key}>
                  {/* Foldable group header: chevron, colour dot, DUE TODAY (1). */}
                  <button
                    type="button"
                    onClick={() =>
                      setFoldedGroups((f) => ({ ...f, [group.key]: !folded }))}
                    aria-expanded={!folded}
                    className="flex items-center gap-2 rounded-lg px-1 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 transition hover:text-slate-700"
                  >
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"
                      className={`h-3.5 w-3.5 transition-transform ${folded ? '-rotate-90' : ''}`}>
                      <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className={`h-2 w-2 rounded-full ${group.dot}`} />
                    {group.label}
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 tabular-nums text-slate-500">
                      {group.tasks.length}
                    </span>
                  </button>

                  {!folded && (
                    <ul className="mt-2 space-y-2">
                      {group.tasks.map((task) => (
                        <ListTaskRow
                          key={task.id}
                          task={task}
                          busy={actions.busyId === task.id}
                          onOpen={setViewing}
                          onToggleDone={(t) => void actions.toggleDone(t)}
                          onToggleImportant={(t) => void actions.toggleImportant(t)}
                          onRemove={(t) => void removeFromList(t)}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              );
            })
          )}
        </div>
      </div>

      <SidePanel
        open={creating}
        title={selected ? `New task in ${selected.name}` : 'New task'}
        description="Describe the work and choose who does it."
        onClose={() => setCreating(false)}
      >
        <TaskForm
          onCancel={() => setCreating(false)}
          onCreated={(task) => void fileNewTask(task)}
        />
      </SidePanel>

      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => { replace(t); setViewing(t); }}
        />
      )}
    </div>
  );
}
